import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ResetpasswordemailserviceService } from '../resetpasswordemail/resetpasswordemailservice.service';

@Component({
  selector: 'resetnewpassword',
  templateUrl: './resetnewpassword.component.html',
  styleUrls: ['./resetnewpassword.component.scss']
})
export class ResetnewpasswordComponent implements OnInit {
  showNewPassword = false;
showConfirmPassword = false;


  resetPasswordForm: FormGroup;
  token: string = '';  // Token extracted from URL
  isSubmitting = false;
  errorMessage = '';
  successMessage = '';

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private resetPasswordService: ResetpasswordemailserviceService
  ) {
    this.resetPasswordForm = this.fb.group(
      {
        newPassword: [
          '',
          [
            Validators.required,
            Validators.minLength(6)  // Changed to 6 to match backend validation
          ]
        ],
        confirmPassword: ['', [Validators.required]]
      },
      { validators: this.passwordsMatchValidator }
    );
  }

  ngOnInit(): void {
    // Extract token from URL query parameters
    this.route.queryParams.subscribe(params => {
      this.token = params['token'];
      
      if (!this.token) {
        this.errorMessage = 'Invalid or missing reset token. Please request a new password reset link.';
      }
    });
  }

  get newPassword() {
    return this.resetPasswordForm.get('newPassword');
  }

  get confirmPassword() {
    return this.resetPasswordForm.get('confirmPassword');
  }

  get newPasswordError(): string {
    if (this.newPassword?.hasError('required')) {
      return 'New password is required';
    }
    if (this.newPassword?.hasError('minlength')) {
      return 'Password must be at least 6 characters';
    }
    return '';
  }

  get confirmPasswordError(): string {
    if (this.confirmPassword?.hasError('required')) {
      return 'Please confirm your password';
    }
    return '';
  }

  passwordsMatchValidator(group: AbstractControl): ValidationErrors | null {
    const pwd = group.get('newPassword')?.value;
    const confirm = group.get('confirmPassword')?.value;
    return pwd && confirm && pwd !== confirm ? { passwordMismatch: true } : null;
  }

  onSubmit(): void {
    // Check if token exists
    if (!this.token) {
      this.errorMessage = 'Invalid reset token. Please request a new password reset link.';
      return;
    }

    // Validate form
    if (this.resetPasswordForm.invalid) {
      this.resetPasswordForm.markAllAsTouched();
      return;
    }

    // Reset messages
    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    const newPassword = this.newPassword?.value;

    // Call the API
    this.resetPasswordService.resetPassword(this.token, newPassword).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        
        // Success response
        this.successMessage = response.message || 
          'Your password has been reset successfully. Redirecting to login...';
        
        // Reset form
        this.resetPasswordForm.reset();
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 3000);
      },
      error: (error) => {
        this.isSubmitting = false;
        
        // Handle different error scenarios
        if (error.status === 400) {
          if (error.error.message?.includes('expired')) {
            this.errorMessage = 'This reset link has expired. Please request a new password reset.';
          } else if (error.error.message?.includes('invalid')) {
            this.errorMessage = 'Invalid reset link. Please request a new password reset.';
          } else if (error.error.message?.includes('6 characters')) {
            this.errorMessage = 'Password must be at least 6 characters long.';
          } else {
            this.errorMessage = error.error.message || 'Invalid request. Please check your input.';
          }
        } else if (error.status === 500) {
          this.errorMessage = 'Server error. Please try again later or contact support.';
        } else if (error.status === 0) {
          this.errorMessage = 'Unable to connect to server. Please check your internet connection.';
        } else {
          this.errorMessage = error.error.message || 'An unexpected error occurred. Please try again.';
        }
        
        console.error('Password reset error:', error);
      }
    });
  }

  goBack(): void {
    // Navigate back to forgot password page
    this.router.navigate(['/reset-password-email']);
  }
}